import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { find, is, propEq } from 'ramda/src';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import Rating from '@components/@core/Rating';
import CheckoutGraph from '@components/CheckoutGraph';
import { UserRoles } from '~/services/config';

const moment = extendMoment(originalMoment);

const CheckoutReportIndividual = () => {
    const params = useParams();
    const navigate = useNavigate();

    // Redux Managed State Variables
    const authUserId = useSelector((state) => state.app.id);
    const authUserRole = useSelector((state) => state.app.app_role_id);
    const companyId = useSelector((state) => state.app.company_info.id);
    const employees = useSelector((state) => state.app.users);
    const individualCheckouts = useSelector((state) => state.manage.companyCheckouts?.users[0]) || [];
    const checkouts = useSelector((state) => state.manage.companyCheckouts?.users[0]?.checkouts) || [];
    const isLoading = useSelector((state) => state.manage.status.includes('pending-gcc'));
    
    const query = queryString.parse(location.search);
    const { requestedId = Number(params.id) || authUserId} = params;
    const isManager = authUserRole < UserRoles.MANAGER;

    const [userId, setUserId] = useState(isManager ? requestedId : authUserId);
    const [date, setDate] = useState(moment());
    const [dateStart, setDateStart] = useState(query?.dateStart ? moment(query.dateStart).format('YYYY-MM-DD') : moment(date).startOf('month').format('YYYY-MM-DD'));
    const [dateEnd, setDateEnd] = useState(query?.dateEnd ? moment(query.dateEnd).format('YYYY-MM-DD') : moment(date).format('YYYY-MM-DD'));

    const user = find(propEq('id', userId), employees) || { profile: {} };
    const dateRange = moment.range(dateStart, dateEnd);
    const dates = Array.from(dateRange.by('day'));
    

    const handleDetail = (item) => {
        const route = {
            pathname: `/hcm/daily-checkout/${item.user_id}`,
            search: `date=${moment(item.checkout_at).format('YYYY-MM-DD')}`,
            state: { lastReview: null },
        };
        return navigate(route);
    };

    const handleFilter = useCallback(
        (type, e) => {
            if (!isManager) return;

            if (type === 'employee') {
                if (e[0].id < 0) return;
                setUserId(e[0].id);
                return;
            }
        },
        []
    );


    useEffect(() => {
        setDateStart(moment(date).startOf('month').format('YYYY-MM-DD'));
        setDateEnd(moment(date).endOf('month').format('YYYY-MM-DD'));
    },[date]);
    
    return (
        <ErrorBoundary className="ind-performance-reports">
            <Filter 
                onChange={handleFilter} 
                defaultIds={[[companyId], [userId]]}  
                multi={[false, false]} 
            />
            <div>
                <CheckoutGraph user={user} date={date} setDate={setDate} graphTitle="Daily Score History" />
                <div className="card mt-3">
                    <div className="py-2 d-none d-md-flex list-header">
                        <div className="d-flex-2">
                            <span className="dsl-m12">Date</span>
                        </div>
                        <div className="d-flex-4">
                            <span className="dsl-m12">Daily Scorecard</span>
                        </div>
                
                        <div className="d-flex-3">
                            <span className="dsl-m12">Status</span>
                        </div>
                        <div className="d-flex-3">
                            <span className="dsl-m12">Score</span>
                        </div>
                        <div className="d-flex-3">
                            <span className="dsl-m12">Date Completed</span>
                        </div>
                    </div>
                    {!isLoading && (
                        dates.map((date) => {
                            const item = find(propEq('checkout_at', date.format('YYYY-MM-DD')), checkouts);
                            if (item?.scorecard_id) {
                                const completed = item.status === 1;
                                const { data } = item;
                                const title = individualCheckouts.daily_scorecards[item.scorecard_id]?.title;
                                return (
                                    <div className="list-item list cursor-pointer" key={item.id} onClick={() => handleDetail(item)}>
                                        <div className="d-flex-2 d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Date</label>
                                            <span className="dsl-b16 cursor-pointer">{moment(item.checkout_at).format('MMM DD')}</span>
                                        </div>
                                        <div className="d-flex-4 truncate-one d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Daily Scorecard</label>
                                            <span className="dsl-b16">{title}</span>
                                        </div>
                                        <div className="d-flex-3 d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Status</label>
                                            <p className="dsl-b16 m-0">{completed ? 'Completed' : 'Incomplete'}</p>
                                        </div>
                                        <div className="d-flex-3 d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Score</label>
                                            {completed ? <Rating score={item.data.score} /> : <span className="dsl-b16">Incomplete</span>}
                                        </div>
                                        <div className="d-flex-3 d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Date Completed</label>
                                            <p className="dsl-b16 m-0">{completed ? moment(data.signoff.time).format('MM/DD/YY hh:mma') : 'Incomplete'}</p>
                                        </div>
                                    </div>
                                );
                            } else if (date <= moment().startOf('day')) {
                                return (
                                    <div className="list-item list" key={date} onClick={() => {}}>
                                        <div className="d-flex-2 d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Date</label>
                                            <span className="dsl-b16">{moment(date).format('MMM DD')}</span>
                                        </div>
                                        <div className="d-flex-4 truncate-one d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Daily Scorecard</label>
                                            <span className="dsl-b16">No Scorecard</span>
                                        </div>
                                        <div className="d-flex-3 d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Status</label>
                                            <p className="dsl-b16 m-0">Not Assigned</p>
                                        </div>
                                        <div className="d-flex-3 d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Score</label>
                                            <span className="dsl-b16">Not Assigned</span>
                                        </div>
                                        <div className="d-flex-3 d-flex ai-center">
                                            <label className="mobile-label d-md-none dsl-m12 m-0">Date Completed</label>
                                            <p className="dsl-b16 m-0">Not Assigned</p>
                                        </div>
                                    </div>
                                );
                            }
                        }
                    ))} 
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default CheckoutReportIndividual;
